import React, { useState } from 'react';
import {
  Form,
  Row,
  Col,
  Button,
  Divider,
  message,
  Input,
  Switch,
  Tooltip
} from 'antd';
import { ReactComponent as SelectSvg } from '@assets/icons/select-menu-item.svg';
import { ReactComponent as EyeOpenSvg } from '@assets/icons/eye-opened.svg';
import { ReactComponent as EyeCloseSvg } from '@assets/icons/eye-closed.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { useOrganization } from '@components/OrganizationBoundary';
import { FormInstance } from 'antd/es/form/Form';
import type { FormValues } from '@components/Modals/ShareAssetModal/ShareAssetModal';

interface ISettingsProps {
  onCloseModal(): void;
  form: FormInstance<FormValues>;
  isSubmitting: boolean;
  isWholeCampaign: boolean;
}

function Settings({
  form,
  onCloseModal,
  isSubmitting,
  isWholeCampaign
}: ISettingsProps) {
  const { currentOrganization } = useOrganization(false);

  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
  const [isPasswordSuccess, setIsPasswordSuccess] = useState<boolean>(false);

  const allowComments = Form.useWatch('allowComments', form);

  const onChangeSwitchPassword = (val: boolean) => {
    setIsShowPassword(val);
    setIsPasswordSuccess(false);
    // reset password
    const password = form.getFieldValue('password');
    if (!val && password) {
      form.resetFields(['password']);
      form.submit();
    }
  };

  const onSubmitPasswordForm = async () => {
    const password = form.getFieldValue('password');
    if (!password) {
      form.setFields([
        {
          name: 'password',
          errors: ['Password required.']
        }
      ]);
      return;
    }
    try {
      setIsPasswordSuccess(false);
      await form.submit();
      message.success({
        content: 'Password added!',
        className: 'message-dark-modal',
        icon: <SuccessSvg className="anticon" />
      });
      setIsPasswordSuccess(true);
    } catch (error: any) {
      if (error?.response?.data?.errorCode === 'feature_not_allowed') {
        form.resetFields(['password', 'addPassword']);
        setIsShowPassword(false);
      }
    }
  };

  const passwordIconRender = (visible: boolean) =>
    visible ? <EyeOpenSvg /> : <EyeCloseSvg />;

  const upgradePassword =
    !isShowPassword &&
    !currentOrganization?.entity?.features.passwordProtectedShareLinks;

  return (
    <div className="share_asset_modal_settings">
      <div>
        <p className="modal_container__copy">Link permissions</p>
        <Form.Item
          className="share_asset_modal_switch"
          colon={false}
          label="Allow comments"
        >
          <Tooltip
            placement="top"
            color="white"
            overlayClassName="share_asset_modal_tooltip"
            title="When disabled, the link is view-only."
          >
            <Form.Item name="allowComments" valuePropName="checked">
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                className="share_asset_modal_switch-control"
                onChange={() => form.submit()}
              />
            </Form.Item>
          </Tooltip>
        </Form.Item>

        <Form.Item
          className="share_asset_modal_switch"
          colon={false}
          label="Show existing comments"
        >
          <Tooltip
            placement="top"
            color="white"
            overlayClassName="share_asset_modal_tooltip"
            title="When disabled, all existing comments will be hidden on this media link."
          >
            <Form.Item name="allowAllComments" valuePropName="checked">
              <Switch
                disabled={!allowComments}
                checkedChildren="On"
                unCheckedChildren="Off"
                className="share_asset_modal_switch-control"
              />
            </Form.Item>
          </Tooltip>
        </Form.Item>

        <Form.Item
          className="share_asset_modal_switch"
          colon={false}
          label="Allow downloads"
          name="allowDownloads"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="On"
            unCheckedChildren="Off"
            className="share_asset_modal_switch-control"
          />
        </Form.Item>
        <Form.Item
          className="share_asset_modal_switch"
          colon={false}
          label="All versions"
          name="allowAllVersions"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="On"
            unCheckedChildren="Off"
            className="share_asset_modal_switch-control"
          />
        </Form.Item>
        {isWholeCampaign && (
          <Form.Item
            className="share_asset_modal_switch"
            colon={false}
            label="Show private folders"
            name="includePrivateFolders"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="On"
              unCheckedChildren="Off"
              className="share_asset_modal_switch-control"
            />
          </Form.Item>
        )}
        <Divider className="modal_container_divider" />
        <p className="modal_container__copy">Link settings</p>
        <Form.Item
          colon={false}
          className="share_asset_modal_switch"
          label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Add password
              {upgradePassword && <UpgradePlanSvg style={{ marginLeft: 8 }} />}
            </div>
          }
          name="addPassword"
          valuePropName="checked"
        >
          <UpgradePlanTooltip
            text=" to a Team plan to set a password."
            isCanVisible={upgradePassword}
            beforeUpgradeClick={() => onCloseModal()}
            innerStyle={{
              transform: 'translate(0, 10px)'
            }}
          >
            <Switch
              disabled={upgradePassword}
              onChange={onChangeSwitchPassword}
              checkedChildren="On"
              unCheckedChildren="Off"
              className="share_asset_modal_switch-control"
            />
          </UpgradePlanTooltip>
        </Form.Item>
        <div>
          {isShowPassword && (
            <Row wrap={false} gutter={12}>
              <Col className="share_asset_modal_form-left">
                <Form.Item name="password">
                  <Input.Password
                    placeholder="Set password"
                    size="large"
                    iconRender={passwordIconRender}
                  />
                </Form.Item>
              </Col>
              <Col className="share_asset_modal_form-right">
                <Button
                  type="primary"
                  htmlType="button"
                  className="submit_form submit_form_btn"
                  size="middle"
                  onClick={onSubmitPasswordForm}
                  icon={
                    isPasswordSuccess && <SelectSvg className="b-icon-select" />
                  }
                  loading={isSubmitting}
                >
                  Save
                </Button>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
